import clsx from 'clsx';
import { Suspense, useEffect } from 'react';
import { Outlet, ScrollRestoration } from 'react-router';
import { ns, useT } from '@/i18n';
import { ExperienceSearchSheet } from '@/features/experiences/search/ExperienceSearchSheet';
import { VehicleSearchSheet } from '@/features/vehicles/search/VehicleSearchSheet';
import { AccountMenu } from './AccountMenu';
import { BottomNav } from './BottomNav';
import { DesktopHeader } from './DesktopHeader';
import { Footer } from './Footer';
import { MobileTopBar } from './MobileTopBar';
import { PageFallback } from './PageFallback';
import { PreferencesPopover } from './PreferencesPopover';
import { useShellOverrides } from './ShellContext';
import { useScrollState } from './useScrollState';
import { useShellHandle } from './useShellHandle';
import { usePageViews } from './usePageViews';
import { useLiveChat } from './useLiveChat';
import { Promos } from './promos/Promos';
import styles from './AppShell.module.css';

/** Root layout: header, bars, footer and the shell overlays around the current page. */
export function AppShell() {
  const handle = useShellHandle();
  const t = useT(ns.shell);
  const scroll = useScrollState();
  usePageViews();
  useLiveChat();
  const overrides = useShellOverrides();
  const bottomNav = (handle.bottomNav ?? true) && !overrides.hideBottomNav;
  const footer = handle.footer ?? true;

  useEffect(() => {
    document.documentElement.dataset.universe = handle.universe;
  }, [handle.universe]);

  return (
    <div className={clsx(styles.app, handle.hero && styles.hero, !bottomNav && styles.noBottomNav)}>
      <a className={styles.skip} href="#main">
        {t('skipToContent')}
      </a>
      <DesktopHeader handle={handle} scroll={scroll} />
      <MobileTopBar handle={handle} scroll={scroll} />
      <main id="main" tabIndex={-1} className={styles.main}>
        <Suspense fallback={<PageFallback />}>
          <Outlet />
        </Suspense>
      </main>
      {footer && <Footer />}
      {bottomNav && <BottomNav handle={handle} mini={scroll.mini} />}
      <VehicleSearchSheet />
      <ExperienceSearchSheet />
      <AccountMenu />
      <PreferencesPopover />
      <Promos banner={bottomNav} />
      <ScrollRestoration />
    </div>
  );
}
